import type { FC } from 'react';
import React from 'react';

import styled from '@emotion/styled';

import { SERIF_FONT_FAMILY, mq } from '@clutter/clean';

import type {
  ActiveStorageAttachment,
  Location__Card,
  PageQuery,
} from '@graphql/platform';

import CARD_DEFAULT_IMAGE from './images/card_default.png';

import { PageCTA } from './cta';
import {
  PAGE_CARD_CTA_CONTAINER,
  PAGE_CARD_CTA_LABEL,
  PageContent,
} from './shared';

const PageCardContainer = styled.div`
  display: flex;
  align-items: center;
  ${mq({
    flexDirection: ['column', null, null, 'row'],
    gap: ['36px', null, null, '19px'],
    margin: ['0', null, null, '0 0 24px'],
    paddingTop: ['0', null, null, '50px'],
  })}
`;

const PageCardText = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  ${mq({
    order: [2, null, null, 1],
    width: ['100%', null, null, '39%'],
    gap: ['24px', null, null, '24px'],
  })}
`;

const PageCardTitle = styled.h3`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 500;
  line-height: 1.2;
  color: black;
  margin: 0;
  ${mq({
    fontSize: ['32px', '32px'],
  })}
`;

const PageCardDescription = styled.p`
  font-weight: 400;
  line-height: 1.2;
  color: black;
  margin: 0;
  ${mq({
    fontSize: ['18px', '24px'],
  })}
`;

const PageCardPhotoContainer = styled.div`
  border-radius: 18px;
  overflow: hidden;
  flex-shrink: 0;
  ${mq({
    order: [1, null, null, 2],
    width: ['100%', null, null, '59%'],
    height: ['auto', null, null, '376px'],
    aspectRatio: ['1', null, null, 'auto'],
  })}
`;

const PageCardPhoto = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PageCard: FC<{
  card: Pick<Location__Card, 'title' | 'description' | 'alt' | 'cta'> & {
    photo?: Pick<ActiveStorageAttachment, 'id' | 'imgixURL'> | null;
  };
}> = ({ card }) => (
  <PageCardContainer>
    <PageCardText>
      <PageCardTitle>{card.title}</PageCardTitle>
      <PageCardDescription>{card.description}</PageCardDescription>
      <PageCTA
        id="card_cta"
        container={PAGE_CARD_CTA_CONTAINER}
        label={card.cta || PAGE_CARD_CTA_LABEL}
      />
    </PageCardText>
    <PageCardPhotoContainer>
      <PageCardPhoto
        src={card.photo?.imgixURL || CARD_DEFAULT_IMAGE}
        alt={card.alt}
      />
    </PageCardPhotoContainer>
  </PageCardContainer>
);

export const PageCards: FC<{
  page: PageQuery['page'];
}> = ({ page }) => {
  if (!page) throw new Error('missing page');

  return (
    <PageContent>
      {page.cards.map((card) => (
        <PageCard key={card.id} card={card} />
      ))}
    </PageContent>
  );
};
